/**
 * idealizedPressure.ts
 *
 * Provides an IDEALIZED mean-sea-level-pressure (MSLP) field built from the
 * four well-known planetary pressure belts (equatorial low, subtropical
 * high, subpolar low, polar high) plus a wavenumber-3 longitudinal
 * perturbation representing planetary (Rossby) waves, which are strongest
 * at mid/high latitudes and weak near the equator.
 *
 * This is a PLACEHOLDER for the real NOAA GFS MSLP field (PRMSL), used only
 * to demonstrate the globe-surface pressure coloring and legend plumbing
 * before real data ingestion is implemented.
 */

const DEG2RAD = Math.PI / 180;

interface PressureBelt {
  latDeg: number;
  hPa: number;
}

/** Belt centers and representative values, degrees latitude 0..90 (mirrored for the Southern Hemisphere). */
const BELTS: PressureBelt[] = [
  { latDeg: 0, hPa: 1009 },
  { latDeg: 30, hPa: 1022 },
  { latDeg: 60, hPa: 1006 },
  { latDeg: 90, hPa: 1024 }
];

function beltBaseline(latAbsDeg: number): number {
  const clamped = Math.min(latAbsDeg, 90);
  for (let i = 0; i < BELTS.length - 1; i++) {
    const a = BELTS[i];
    const b = BELTS[i + 1];
    if (clamped >= a.latDeg && clamped <= b.latDeg) {
      const f = (clamped - a.latDeg) / (b.latDeg - a.latDeg);
      // Smoothstep interpolation for rounder belt transitions than linear.
      const smooth = f * f * (3 - 2 * f);
      return a.hPa + (b.hPa - a.hPa) * smooth;
    }
  }
  return BELTS[BELTS.length - 1].hPa;
}

/** Samples the idealized MSLP field (hPa) at a given longitude/latitude (degrees). */
export function sampleIdealizedMslpHpa(lonDeg: number, latDeg: number): number {
  const latAbsDeg = Math.abs(latDeg);
  const baseline = beltBaseline(latAbsDeg);

  // Wave amplitude MUST vanish at the poles: every longitude converges to the same physical
  // point there, so any longitude-dependent term that doesn't vanish creates a "pizza slice"
  // discontinuity artifact. It should also vanish at the equator (real planetary waves are a
  // mid-latitude phenomenon), so it peaks at 45 degrees via sin(2 * latAbsDeg).
  const waveNumber = 3;
  const waveAmplitude = 7 * Math.sin(2 * latAbsDeg * DEG2RAD);
  const wave = waveAmplitude * Math.cos(waveNumber * lonDeg * DEG2RAD);

  return baseline + wave;
}
